import type { ReactElement } from "react";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import type { TMDBSearchResult } from "../hooks/useTMDB";

interface TMDBSearchResultsProps {
  results: TMDBSearchResult[];
  loadingId: number | null;
  onSelect: (id: number) => Promise<void>;
}

/**
 * TMDB Search Results Component
 */
export function TMDBSearchResults({ results, loadingId, onSelect }: Readonly<TMDBSearchResultsProps>): ReactElement {
  return (
    <div className="bp-6">
      <h4 className="text-md font-semibold text-white mb-4">Search Results</h4>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {results.map((movie) => {
          const isLoading = loadingId === movie.id;

          return (
            <button
              key={movie.id}
              onClick={() => {
                void onSelect(movie.id);
              }}
              disabled={isLoading}
              className={`w-full flex items-start gap-4 p-6 rounded-lg transition-colors text-left ${
                isLoading
                  ? "bg-lumi-accent/20 border border-lumi-accent cursor-wait"
                  : "bg-blue-600/0 border border-blue-600/0 hover:bg-blue-600/10 hover:border-blue-600/60"
              }`}
            >
              {movie.poster_path && (
                <img
                  src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                  alt={movie.title}
                  className="w-16 h-auto object-cover rounded"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h5 className="font-semibold text-white truncate">{movie.title}</h5>
                  {movie.release_date && (
                    <span className="text-sm text-neutral-400 flex-shrink-0">({movie.release_date.split("-")[0]})</span>
                  )}
                </div>
                <p className="text-sm text-neutral-400 line-clamp-2 mt-1">{movie.overview}</p>
                {movie.vote_average > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-lumi-accent">★</span>
                    <span className="text-sm text-neutral-400">{movie.vote_average.toFixed(1)}/10</span>
                  </div>
                )}
                {isLoading && (
                  <div className="flex items-center gap-2 mt-2 text-lumi-accent">
                    <LoadingSpinner size="sm" className="!border-lumi-accent" />
                    <span className="text-sm font-medium">Auto-filling form...</span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
